import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import { comics } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const comic = comics.find((c) => c.id === id);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-semibold text-foreground-900 mb-2">漫画未找到</h2>
            <p className="text-foreground-500 mb-6">抱歉，您要查看的漫画不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.categorySlug === comic.categorySlug && c.id !== comic.id)
    .slice(0, 4);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩漫画网`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: comic.updateDate,
    image: comic.coverImage,
    publisher: {
      '@type': 'Organization',
      name: '韩漫画网',
    },
  };

  return (
    <>
      <SEOHead
        title={`${comic.title} | ${comic.author}`}
        description={comic.description}
        keywords={`${comic.title},${comic.author},韩国漫画,韩漫在线阅读,${comic.category}`}
        canonicalUrl={`https://www.hanmanhua.com/article/${comic.id}`}
        ogType="article"
        ogImage={comic.coverImage}
        structuredData={structuredData}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: `${comic.category}漫画`, href: `/category/${comic.categorySlug}` },
              { label: comic.title },
            ]}
          />

          {/* Article Header */}
          <article className="py-6 md:py-8">
            <div className="flex flex-col md:flex-row gap-6 md:gap-10">
              {/* Cover */}
              <div className="w-full md:w-[280px] flex-shrink-0">
                <div className="aspect-[3/4] rounded-lg overflow-hidden">
                  <img
                    src={comic.coverImage}
                    alt={`${comic.title} - 韩国漫画封面`}
                    title={`${comic.title} - ${comic.author}作品`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${
                    comic.status === '连载中' ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                  {comic.isHot && (
                    <span className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">
                      热门
                    </span>
                  )}
                  <span className="text-xs text-foreground-400 bg-background-100 px-2 py-0.5 rounded-full">
                    {comic.category}
                  </span>
                </div>

                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-2">
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-4">
                  作者：<strong className="text-foreground-700">{comic.author}</strong>
                </p>

                <div className="flex items-center gap-4 mb-5">
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500"></i>
                    <span className="text-sm font-semibold text-foreground-900">{comic.rating}</span>
                    <span className="text-xs text-foreground-400">/ 10</span>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-foreground-500">
                    <i className="ri-eye-line"></i>
                    {(comic.views / 10000).toFixed(1)}万 阅读
                  </div>
                  <div className="text-sm text-foreground-500">
                    更新于 {comic.updateDate}
                  </div>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-5">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-600 border border-background-200"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                {/* Description */}
                <div className="bg-background-100 rounded-lg p-4 mb-6">
                  <p className="text-sm text-foreground-700 leading-relaxed">{comic.longDescription}</p>
                </div>

                {/* Read Button */}
                <button
                  onClick={() => {
                    if (comic.chapters.length > 0) {
                      const firstChapter = comic.chapters[0];
                      window.scrollTo({ top: document.getElementById('chapters')?.offsetTop, behavior: 'smooth' });
                    }
                  }}
                  className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-white font-medium rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-play-circle-line text-lg"></i>
                  开始阅读
                </button>
              </div>
            </div>
          </article>

          {/* Chapters Section */}
          <section id="chapters" className="py-10 border-t border-background-200">
            <div className="flex items-center gap-3 mb-6">
              <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-900">
                章节列表
              </h2>
              <span className="text-sm text-foreground-400">
                共 {comic.chapters.length} 话
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {comic.chapters.map((chapter) => (
                <div
                  key={chapter.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-background-100 hover:bg-background-200 transition-colors cursor-pointer group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-bookmark-line text-primary-500 text-sm"></i>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground-800 truncate group-hover:text-primary-500 transition-colors">
                        {chapter.title}
                      </p>
                      <p className="text-xs text-foreground-400">{chapter.updateDate}</p>
                    </div>
                  </div>
                  <i className="ri-arrow-right-s-line text-foreground-400 group-hover:text-primary-500 transition-colors w-5 h-5 flex items-center justify-center flex-shrink-0"></i>
                </div>
              ))}
            </div>
          </section>

          {/* Related Comics */}
          {relatedComics.length > 0 && (
            <section className="py-10 border-t border-background-200">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-900">
                  相关推荐
                </h2>
                <Link
                  to={`/category/${comic.categorySlug}`}
                  className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  更多{comic.category}漫画 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}